import api from './api';

// Helper function to handle API errors
const handleApiError = (error, defaultMessage = 'An error occurred') => {
  console.error('API Error:', error);
  
  if (error.response) {
    // Server responded with error status
    const status = error.response.status;
    const data = error.response.data;
    
    if (status === 401) {
      throw new Error('Authentication required. Please log in.');
    } else if (status === 403) {
      throw new Error('Access denied. You do not have permission to perform this action.');
    } else if (status === 404) {
      throw new Error('Resource not found.');
    } else if (status >= 500) {
      throw new Error('Server error. Please try again later.');
    } else if (data && data.detail) {
      throw new Error(data.detail);
    } else if (data && data.error) {
      throw new Error(data.error);
    }
  } else if (error.request) {
    // Network error
    throw new Error('Network error. Please check your connection and try again.');
  }
  
  throw new Error(defaultMessage);
};

// Safe API wrapper
const safeApiCall = async (apiCall, defaultMessage) => {
  try {
    const response = await apiCall();
    return response;
  } catch (error) {
    handleApiError(error, defaultMessage);
  }
};

const tradingService = {
  // Trading Strategies
  getStrategies: () => 
    safeApiCall(
      () => api.get('/api/data_predator/trading/strategies/'),
      'Failed to load trading strategies'
    ),
  
  createStrategy: (strategyData) => 
    safeApiCall(
      () => api.post('/api/data_predator/trading/strategies/', strategyData),
      'Failed to create trading strategy'
    ),
  
  getStrategy: (id) => 
    safeApiCall(
      () => api.get(`/api/data_predator/trading/strategies/${id}/`),
      'Failed to load strategy details'
    ),
  
  updateStrategy: (id, strategyData) => 
    safeApiCall(
      () => api.put(`/api/data_predator/trading/strategies/${id}/`, strategyData),
      'Failed to update trading strategy'
    ),
  
  deleteStrategy: (id) => 
    safeApiCall(
      () => api.delete(`/api/data_predator/trading/strategies/${id}/`),
      'Failed to delete trading strategy'
    ),
  
  activateStrategy: (id) => 
    safeApiCall(
      () => api.post(`/api/data_predator/trading/strategies/${id}/activate/`),
      'Failed to activate trading strategy'
    ),
  
  deactivateStrategy: (id) => 
    safeApiCall(
      () => api.post(`/api/data_predator/trading/strategies/${id}/deactivate/`),
      'Failed to deactivate trading strategy'
    ),
  
  pauseStrategy: (id) => 
    safeApiCall(
      () => api.post(`/api/data_predator/trading/strategies/${id}/pause/`),
      'Failed to pause trading strategy'
    ),
  
  // Trading Positions
  getPositions: (params = {}) => 
    safeApiCall(
      () => api.get('/api/data_predator/trading/positions/', { params }),
      'Failed to load trading positions'
    ),
  
  getPosition: (id) => 
    safeApiCall(
      () => api.get(`/api/data_predator/trading/positions/${id}/`),
      'Failed to load position details'
    ),
  
  // Trading Logs
  getLogs: (params = {}) => 
    safeApiCall(
      () => api.get('/api/data_predator/trading/logs/', { params }),
      'Failed to load trading logs'
    ),
  
  getLog: (id) => 
    safeApiCall(
      () => api.get(`/api/data_predator/trading/logs/${id}/`),
      'Failed to load log details'
    ),
  
  // Dashboard data
  getTradingDashboard: () => 
    safeApiCall(
      () => api.get('/api/data_predator/trading/dashboard/'),
      'Failed to load trading dashboard data'
    ),
  
  // Real-time status
  getSystemStatus: () => 
    safeApiCall(
      () => api.get('/api/data_predator/trading/status/'),
      'Failed to load system status'
    ),

  // Account Balance
  getAccountBalance: (broker, accountNumber, useCache = true) => {
    // console.log('🔗 getAccountBalance 호출:', { broker, accountNumber, useCache });
    return safeApiCall(
      () => {
        const request = api.get('/api/data_predator/account-balance/', {
          params: {
            broker,
            account_number: accountNumber,
            use_cache: useCache
          }
        });
        // console.log('📤 단일 계좌 잔고 API 요청:', request);
        return request;
      },
      'Failed to load account balance'
    );
  },

  getMultipleAccountBalances: (accounts, useCache = true) => {
    // console.log('🔗 getMultipleAccountBalances 호출:', { accounts, useCache });
    // console.log('🌐 API Base URL:', api.defaults.baseURL);
    // console.log('📍 Full URL:', `${api.defaults.baseURL}/api/data_predator/account-balance/`);
    
    return safeApiCall(
      async () => {
        // console.log('📤 다중 계좌 잔고 API 요청 시작');
        const response = await api.post('/api/data_predator/account-balance/', {
          accounts,
          use_cache: useCache
        });
        // console.log('✅ 다중 계좌 잔고 API 응답:', response);
        return response;
      },
      'Failed to load multiple account balances'
    );
  },

  // Stock Holdings
  getStockHoldings: (broker, accountNumber, useCache = true) => {
    // console.log('🔗 getStockHoldings 호출:', { broker, accountNumber, useCache });
    return safeApiCall(
      () => {
        const request = api.get('/api/data_predator/stock-holdings/', {
          params: {
            broker,
            account_number: accountNumber,
            use_cache: useCache
          }
        });
        // console.log('📤 보유 주식 API 요청:', request);
        return request;
      },
      'Failed to load stock holdings'
    );
  }
};

export default tradingService;
